import { type FC, type FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { SingleValue } from 'react-select';
import type { Animal, AnimalSex } from '@/model/Animal.model';
import { getSexOptions } from '@/model/Animal.utils';
import { featuresSelector } from '@/support/Selectors';
import Select, { type SelectOption } from '@/components/UI/Select/Select';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface UpdateSexProps {
  subject: Animal | Array<Animal>;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

const UpdateSex: FC<UpdateSexProps> = ({ subject, handleCallback, closeModal }) => {
  const [sex, setSex] = useState<AnimalSex>('m');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);

  const what = Array.isArray(subject) && subject.length > 1 ? 'animals' : 'animal';
  const whatDemonstrative = what === 'animals' ? 'these' : 'this';

  const submitForm = (e: FormEvent) => {
    e.preventDefault();
    if (!unknownSexEnabled && sex === 'u') {
      setError('Unknown sex is not available');
      return;
    }
    setIsLoading(true);
    const subjects = Array.isArray(subject) ? subject : [subject];
    const animalIds = subjects.map((s) => s.id);
    Http.patch(apiRoute('animals.sex.bulk.update'), { animal_ids: animalIds, sex })
      .then(() => handleCallback('Successfully updated sex'))
      .catch((e) => setError(e.response?.data?.message ?? 'Unknown Error'))
      .finally(() => setIsLoading(false));
  };

  const defaultOption = sexOptions.find(({ value }) => value === sex);

  return (
    <form onSubmit={submitForm} className="ui__narrow-modal" data-testid="update-sex-form">
      <div className="mb3 tc">
        <h3 className="normal f4 pb2" data-testid="update-sex-heading">
          Update sex of {whatDemonstrative} {what}?
        </h3>
      </div>
      {error && (
        <p className="red" data-testid="update-sex-error">
          {error}
        </p>
      )}
      <div className="mb3" data-testid="update-sex-select">
        <Select
          defaultValue={defaultOption}
          options={sexOptions}
          isMulti={false}
          onChange={(option: SingleValue<SelectOption<string>>) => {
            if (option) {
              setSex(option.value as AnimalSex);
            }
          }}
          name="sex"
        />
      </div>
      <div>
        <button type="submit" disabled={isLoading} className="button w-100" data-testid="update-sex-submit">
          Update sex
        </button>
        <button
          type="button"
          className="button plain db w-100 mt2"
          onClick={closeModal}
          data-testid="update-sex-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UpdateSex;
